import {
    Component,
    computed,
    inject,
    input,
    output,
    OnInit,
} from '@angular/core';

import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SearchStore } from '@app/stores/searchStore';
import { debounceTime, distinctUntilChanged, skip } from 'rxjs';
import { untilDestroyed } from '@ngneat/until-destroy';

@Component({
    selector: 'app-table-pagination',
    standalone: true,
    imports: [
        MatPaginatorModule,
        MatIconModule,
        MatButtonModule,
        MatInputModule,
        MatSelectModule,
        MatFormFieldModule,
        MatTooltipModule,
        TranslateModule,
        FormsModule,
        ReactiveFormsModule,
    ],
    templateUrl: './table-pagination.component.html',
    styleUrls: ['./table-pagination.component.scss'],
})
export class TablePaginationComponent implements OnInit {
    private searchStore = inject(SearchStore);
    private translate = inject(TranslateService);
    // Inputs
    loading = input<boolean>(false);
    pageSizeOptions = input<number[]>([10, 20, 50, 100]);

    // Outputs
    pageChange = output<PageEvent>();

    // Computed values from search state
    currentOffset = computed(() => this.searchStore.pagination().offset);
    currentLimit = computed(() => this.searchStore.pagination().limit);
    totalItems = computed(() => this.searchStore.pagination().totalCount);
    hasMore = computed(() => this.searchStore.pagination().hasMore);

    // Computed pagination values
    isCapped = computed(() => this.searchStore.pagination().totalCountIsCapped);
    /** "10.000+" when the backend capped counting, exact number otherwise */
    totalItemsDisplay = computed(() => {
        const total = this.totalItems().toLocaleString();
        return this.isCapped() ? `${total}+` : total;
    });

    currentPageSize = computed(() => this.currentLimit());
    currentPageNumber = computed(
        () => Math.floor(this.currentOffset() / this.currentLimit()) + 1
    );
    totalPages = computed(() =>
        Math.max(1, Math.ceil(this.totalItems() / this.currentLimit()))
    );

    isFirstPage = computed(() => this.currentOffset() === 0);
    isLastPage = computed(
        () =>
            !this.hasMore() ||
            (!this.isCapped() && this.currentPageNumber() >= this.totalPages())
    );

    getItemsRangeText = computed(() => {
        const offset = this.currentOffset();
        const limit = this.currentLimit();
        const total = this.totalItems();

        if (total === 0) {
            return this.translate.instant('PAGINATION.NO_ITEMS');
        }

        const start = offset + 1;
        const end = Math.min(offset + limit, total);

        return this.translate.instant('PAGINATION.RANGE_TEXT', {
            start: start,
            end: end,
            total: this.isCapped() ? `${total.toLocaleString()}+` : total,
        });
    });

    pageControl: FormControl = new FormControl('1');

    ngOnInit() {
        this.pageControl.valueChanges
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                skip(1) // Skip initial to prevent double fetch on load
            )
            .subscribe(value => {
                this.onPageInputChange(value);
            });

        // Initialize the form control with the actual current page
        // This ensures the display is correct without triggering valueChanges
        this.pageControl.setValue(this.currentPageNumber().toString(), {
            emitEvent: false,
        });
    }

    onPageSizeChange(newPageSize: number): void {
        this.searchStore.updatePagination({
            limit: newPageSize,
            offset: 0, // Reset to first page when changing page size
        });

        // Trigger data refresh with new pagination settings
        this.searchStore.triggerDataRefresh();

        this.pageChange.emit({
            pageIndex: 0,
            pageSize: newPageSize,
            length: this.totalItems(),
        });
    }

    onPageInputChange(pageNumber: number): void {
        // beyond the 10 000 counting cap the page count is a lower bound —
        // the backend serves deeper pages correctly, so allow jumping there
        const withinBounds =
            pageNumber >= 1 &&
            (pageNumber <= this.totalPages() || this.isCapped());
        if (withinBounds) {
            const newOffset = (pageNumber - 1) * this.currentLimit();
            this.searchStore.goToPage(newOffset);

            // Trigger data refresh with new pagination settings
            this.searchStore.triggerDataRefresh();

            this.pageChange.emit({
                pageIndex: pageNumber - 1,
                pageSize: this.currentLimit(),
                length: this.totalItems(),
            });
        } else {
            // Reset to current page if invalid
            this.pageControl.patchValue(this.currentPageNumber().toString());
        }
    }

    goToFirstPage(): void {
        this.searchStore.goToPage(0);

        // Trigger data refresh with new pagination settings
        this.searchStore.triggerDataRefresh();

        this.pageChange.emit({
            pageIndex: 0,
            pageSize: this.currentLimit(),
            length: this.totalItems(),
        });
    }

    goToPrevPage(): void {
        this.searchStore.goToPreviousPage();

        // Trigger data refresh with new pagination settings
        this.searchStore.triggerDataRefresh();

        this.pageChange.emit({
            pageIndex: this.currentPageNumber() - 2, // -2 because we already went back one page
            pageSize: this.currentLimit(),
            length: this.totalItems(),
        });
    }

    goToNextPageHandler(): void {
        this.searchStore.goToNextPage();

        // Trigger data refresh with new pagination settings
        this.searchStore.triggerDataRefresh();

        this.pageChange.emit({
            pageIndex: this.currentPageNumber(), // currentPageNumber will be the new page after goToNextPage
            pageSize: this.currentLimit(),
            length: this.totalItems(),
        });
    }

    goToLastPage(): void {
        const lastPageOffset = (this.totalPages() - 1) * this.currentLimit();
        this.searchStore.goToPage(lastPageOffset);

        // Trigger data refresh with new pagination settings
        this.searchStore.triggerDataRefresh();

        this.pageChange.emit({
            pageIndex: this.totalPages() - 1,
            pageSize: this.currentLimit(),
            length: this.totalItems(),
        });
    }
}
